import {TradingInstrument} from "../trading-instruments/trading-instrument.class";
import {isVoid} from "../utils";
import * as Enumerable from "linq";
import {QuoteGeneratorLoadedStreamDto} from "../shell-communication/shell-dto-protocol";
import {TradingInstrumentsService} from "../trading-instruments/trading-instruments-service.interface";
import {HistoryDatasetDescriptor} from "../shell-communication/shell-operations-protocol";
import {DateTime} from "luxon";

export type QuoteGeneratorMode = 'Manual' | 'Random' | 'History' | 'Script';

export type QuoteGeneratorQuoteSource = 'Theoretical' | 'Live' | 'Historical';


export class QuoteStream {

    constructor(tradingInstrument: TradingInstrument) {
        this._tradingInstrument = tradingInstrument;

        const steps = [
            0.01,
            0.02,
            0.03,
            0.04,
            0.05,
            0.10,
            0.15,
            0.20,
            0.25,
            0.30,
            0.35,
            0.40,
            0.45,
            0.50,
            1.00,
            2.00,
            3.00,
            4.00,
            5.00,
            6.00,
            7.00,
            8.00,
            9.00,
            10.00,
            20.00,
            30.00,
            40.00,
            50.00,
            60.00,
            70.00,
            80.00,
            90.00,
            10.00,
        ];

        this.quoteStepList = steps;

        this.quoteStep = this._tradingInstrument.ticker === 'SPX' ? 10 : 1; // this.quoteStepList[0];
    }

    private readonly _tradingInstrument: TradingInstrument;

    quoteStepList: number[] = [];

    quoteStep: number;

    trendBias = 0;

    jumpToDate: string;

    jumpStep: number = 1;

    get jumpStepLabel(): string {
        const item = this.jumpStepList.find(x => x.value === this.jumpStep);
        return item ? item.step : `${this.jumpStep}m`;
    }

    jumpStepList: { step: string, value: number }[] = [
        {step: '1m', value: 1},
        {step: '5m', value: 5},
        {step: '15m', value: 15},
        {step: '30m', value: 30},
        {step: '1h', value: 60},
        {step: '1d', value: 1440},
    ];

    microView: boolean;

    get isShowingAtm(): boolean {
        return this.mode === 'Manual' && this.quoteSource === 'Live';
    };

    menuItems = [
        {text: 'Reset To Market'},
    ]

    get ticker(): string {
        return this._tradingInstrument.ticker;
    }

    get tickerDisplayName(): string {
        return this._tradingInstrument.displayName;
    }

    mode: QuoteGeneratorMode = 'Manual';

    quoteSource: QuoteGeneratorQuoteSource;

    get quoteSourceList(): QuoteGeneratorQuoteSource[] {
        const list: QuoteGeneratorQuoteSource[] = [];
        list.push('Theoretical');
        if (this.mode === 'Manual') {
            list.push('Live');
        }
        if (this.mode === 'History') {
            list.push('Historical');
        }
        return list;
    }

    historyDelta = 0;

    lastPrice = 1000;

    lastTimestamp: string;

    frequency = 500;

    customScript: string;

    savedScript: any;

    progress: number;

    get isManualMode(): boolean {
        return this.mode === 'Manual';
    }

    get isRandomMode(): boolean {
        return this.mode === 'Random';
    }

    get isNavigationalMode(): boolean {
        return !this.isManualMode && !this.isRandomMode;
    }

    get isHistoryMode(): boolean {
        return this.mode === 'History';
    }

    get isScriptMode(): boolean {
        return this.mode === 'Script';
    }

    get isDatasetSelected(): boolean {
        return !isVoid(this.dataset);
    }

    get isFastClickMode(): boolean {
        return (this.mode === 'Manual' && this.quoteSource === 'Live')
            || this.mode === 'History';
    }

    isRunning: boolean;

    dataset: HistoryDatasetDescriptor;

    atmDistance: number;

    toggleRunning() {
        this.isRunning = !this.isRunning;
    }

    private _rthStart = DateTime.fromObject({hour: 9, minute: 31});
    private _rthEnd = DateTime.fromObject({hour: 16, minute: 0});

    updateTimestamp(side: 1 | -1) {

        const timeJump = this.jumpStep * side;

        const lastTimestamp = DateTime.fromISO(this.lastTimestamp);
        let targetTimestamp = lastTimestamp.plus({minutes: timeJump});

        const isoTimestamp = targetTimestamp.toISO()

        if (isoTimestamp > this.dataset.endPoint || isoTimestamp < this.dataset.startPoint) {
            return;
        }

        // Convert times to minutes since midnight for comparison
        const targetTimeOfDay = targetTimestamp.hour * 60 + targetTimestamp.minute;
        const rthStartMinutes = this._rthStart.hour * 60 + this._rthStart.minute;
        const rthEndMinutes = this._rthEnd.hour * 60 + this._rthEnd.minute;

        const afterEnd = targetTimeOfDay > rthEndMinutes;
        const beforeStart = targetTimeOfDay < rthStartMinutes;
        const isHoliday = targetTimestamp.weekday == 6 || targetTimestamp.weekday == 7;
        const isOutside = afterEnd || beforeStart || isHoliday;

        if (isOutside) {
            if (this.jumpStep === 1440) {
                do {
                    targetTimestamp = targetTimestamp.plus({days: 1 * side});
                } while (targetTimestamp.weekday == 6 || targetTimestamp.weekday == 7);

            } else {
                // Use cursor's date + 1 day, then set to RTH start time
                const isAtRthStart = lastTimestamp.hour == this._rthStart.hour && lastTimestamp.minute == this._rthStart.minute;
                const isAtRthEnd = lastTimestamp.hour == this._rthEnd.hour && lastTimestamp.minute == this._rthEnd.minute;

                do {

                    if (side === 1) {

                        if (isAtRthEnd) {
                            targetTimestamp = targetTimestamp
                                .startOf('day')
                                .plus({days: 1})
                                .set({
                                    hour: this._rthStart.hour,
                                    minute: this._rthStart.minute,
                                    second: 0,
                                    millisecond: 0
                                });
                        } else {
                            targetTimestamp = targetTimestamp
                                .set({
                                    hour: this._rthEnd.hour,
                                    minute: this._rthEnd.minute,
                                    second: 0,
                                    millisecond: 0
                                });
                        }
                    } else if (side == -1) {
                        if (isAtRthStart) {
                            targetTimestamp = targetTimestamp
                                .startOf('day')
                                .minus({days: 1})
                                .set({
                                    hour: this._rthEnd.hour,
                                    minute: this._rthEnd.minute,
                                    second: 0,
                                    millisecond: 0
                                });
                        } else {
                            targetTimestamp = targetTimestamp
                                .set({
                                    hour: this._rthStart.hour,
                                    minute: this._rthStart.minute,
                                    second: 0,
                                    millisecond: 0
                                });
                        }
                    }

                } while (targetTimestamp.weekday == 6 || targetTimestamp.weekday == 7);
            }
        }

        const resultTimestamp = targetTimestamp.toISO();
        this.lastTimestamp = resultTimestamp;
    }

    static fromDto(dto: QuoteGeneratorLoadedStreamDto, tiService: TradingInstrumentsService): QuoteStream {
        const ti = tiService.getInstrumentByTicker(dto.ticker);
        const stream = new QuoteStream(ti);
        stream.mode = dto.quoteMode as any;
        stream.isRunning = dto.isRunning;
        stream.progress = dto.progress;
        stream.lastPrice = dto.lastQuote?.lastPx;
        stream.frequency = dto.frequency;
        stream.quoteStep = dto.quoteStep;
        stream.quoteSource = dto.quoteSource as any;

        if (stream.mode === 'History') {
            stream.historyDelta = dto.lastQuote?.epochDelta ?? 0;
            stream.dataset = {startPoint: dto.historyStartPoint, endPoint: dto.historyEndPoint} as any;
            stream.jumpStep = dto.historyJumpStep;
            stream.jumpToDate = dto.historyJumpToDatapoint;
            stream.lastTimestamp = dto.historyCursor;
        }

        return stream;
    }

    updateLastPrice(side: 1 | -1) {
        const priceJump = this.quoteStep * side;
        this.lastPrice += priceJump;
    }

    updateDelta(side: number) {
        const deltaJump = this.quoteStep * side;
        this.historyDelta += deltaJump;
    }
}
